import React from "react"
import { RouterProvider, createBrowserRouter } from "react-router"

import { docsRoutes } from "@/app/docs/docs-routes"
import { ErrorPage } from "@/app/error"
import NotFound from "@/app/not-found"

const router = createBrowserRouter([
  { path: "/", lazy: () => import("./page") },
  {
    path: "/:username",
    lazy: () => import("./[username]/layout"),
    errorElement: <ErrorPage />,
    children: [
      {
        path: "",
        lazy: () => import("./[username]/page"),
      },
      {
        path: ":projectName",
        lazy: () => import("./[username]/[project-name]/layout"),
        children: [
          {
            path: "",
            lazy: () => import("./[username]/[project-name]/page"),
          },
          {
            path: "settings",
            lazy: () => import("./[username]/[project-name]/settings/layout"),
            children: [
              {
                path: "",
                lazy: () => import("./[username]/[project-name]/settings/page"),
              },
              {
                path: "billing",
                lazy: () =>
                  import("./[username]/[project-name]/settings/billing/page"),
              },
            ],
          },
          {
            path: "metrics",
            lazy: () => import("./[username]/[project-name]/metrics/layout"),
            children: [
              {
                path: "",
                lazy: () => import("./[username]/[project-name]/metrics/page"),
              },
              {
                path: "overview",
                lazy: () =>
                  import("./[username]/[project-name]/metrics/overview_2"),
              },
              {
                path: "details",
                lazy: () =>
                  import("./[username]/[project-name]/metrics/details/page"),
              },
            ],
          },
          {
            path: "relays",
            lazy: () => import("./[username]/[project-name]/relays/layout"),
            children: [
              {
                path: "",
                lazy: () => import("./[username]/[project-name]/relays/page"),
              },
              {
                path: "setup",
                lazy: () =>
                  import("./[username]/[project-name]/relays/setup/page"),
              },
              {
                path: "settings",
                lazy: () =>
                  import("./[username]/[project-name]/relays/settings/page"),
              },
            ],
          },
          {
            path: "dns",
            lazy: () => import("./[username]/[project-name]/dns/layout"),
            children: [
              {
                path: "",
                lazy: () => import("./[username]/[project-name]/dns/page"),
              },
              {
                path: "setup",
                lazy: () =>
                  import("./[username]/[project-name]/dns/setup/page"),
              },
              {
                path: "settings",
                lazy: () =>
                  import("./[username]/[project-name]/dns/settings/page"),
              },
            ],
          },
          {
            path: "access",
            lazy: () => import("./[username]/[project-name]/access/layout"),
            children: [
              {
                path: "",
                lazy: () => import("./[username]/[project-name]/access/page"),
              },
              {
                path: "nodes",
                lazy: () =>
                  import("./[username]/[project-name]/access/nodes/page"),
              },
              {
                path: "ssh-keys",
                lazy: () =>
                  import("./[username]/[project-name]/access/ssh-keys/page"),
              },
            ],
          },
          {
            path: "deploys",
            lazy: () => import("./[username]/[project-name]/deploys/layout"),
            children: [
              {
                path: "",
                lazy: () => import("./[username]/[project-name]/deploys/page"),
              },
              {
                path: ":workflowId",
                lazy: () =>
                  import(
                    "./[username]/[project-name]/deploys/[workflow-id]/page"
                  ),
              },
            ],
          },
          {
            path: "simulations",
            lazy: () =>
              import("./[username]/[project-name]/simulations/layout"),
            children: [
              {
                path: "",
                lazy: () =>
                  import("./[username]/[project-name]/simulations/page"),
              },
              {
                path: "workflow",
                children: [
                  {
                    path: ":workflowId",
                    lazy: () =>
                      import(
                        "./[username]/[project-name]/simulations/workflow/[workflow-id]/page"
                      ),
                  },
                ],
              },
            ],
          },
          {
            path: "workflows",
            lazy: () => import("./[username]/[project-name]/workflows/layout"),
            children: [
              {
                path: "",
                lazy: () =>
                  import("./[username]/[project-name]/workflows/page"),
              },
              {
                path: ":workflowId",
                lazy: () =>
                  import(
                    "./[username]/[project-name]/workflows/[workflow-id]/page"
                  ),
              },
            ],
          },
        ],
      },
      {
        path: "settings",
        lazy: () => import("./[username]/settings/layout"),
        children: [
          {
            path: "",
            lazy: () => import("./[username]/settings/page"),
          },
        ],
      },

      {
        path: "projects/new",
        lazy: () => import("./[username]/projects/new/page"),
      },
      {
        path: ":projectName/simulations/trace",
        children: [
          {
            path: ":traceId",
            lazy: () =>
              import(
                "./[username]/[project-name]/simulations/trace/[trace-id]/layout"
              ),
            children: [
              {
                path: "",
                lazy: () =>
                  import(
                    "./[username]/[project-name]/simulations/trace/[trace-id]/page"
                  ),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/login",
    children: [
      { path: "", lazy: () => import("./login/page") },
      { path: "forgot", lazy: () => import("./login/forgot/page") },
      { path: "reset/:token", lazy: () => import("./login/reset/page") },
    ],
  },
  {
    path: "invite/:code",
    lazy: () => import("./invite/page"),
  },
  {
    path: "/verify/:token",
    lazy: () => import("./verify/page"),
  },
  {
    path: "/signup",
    lazy: () => import("./signup/page"),
  },

  // static site bits
  {
    path: "/docs",
    lazy: () => import("./docs/layout"),
    // see frontend/src/app/docs/docs-routes.tsx for details
    children: docsRoutes,
  },
  {
    path: "/pricing",
    lazy: () => import("./pricing/page"),
  },
  {
    path: "legal",
    lazy: () => import("./legal/layout"),
    children: [
      {
        path: "",
        lazy: () => import("./legal/page"),
      },
      {
        path: "privacy",
        lazy: () => import("./legal/privacy/page"),
      },
      {
        path: "terms",
        lazy: () => import("./legal/terms/page"),
      },
    ],
  },
  {
    path: "/style",
    lazy: () => import("./style/page"),
  },
  {
    path: "*",
    element: <NotFound />,
  },
])

export function Router() {
  return <RouterProvider router={router} />
}
